import { queryClient } from '@/router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'

import { useDialog } from '@/lib/hooks/use-dialog'
import { UserRole } from '@/lib/queries/business/types'
import { updateUserMutation } from '@/lib/queries/business/v1/user/mutations'
import { getMembersQuery, getPendingMembersQuery } from '@/lib/queries/business/v1/user/queries'

import { MEMBERS_PER_PAGE, TabItemValues } from './constants'
import { type MembersProps } from './members'

export type UseMembersParams = Pick<MembersProps, 'user'>

export const useMembers = ({ user }: UseMembersParams) => {
  const [currentTabValue, setCurrentTabValue] = useState<TabItemValues>(TabItemValues.members)
  const [page, setPage] = useState(0)
  const [checkedMemberIds, setCheckedMemberIds] = useState<Set<number>>(new Set())

  const { data: dataMembers } = useQuery(getMembersQuery)
  const { data: dataPendingMembers } = useQuery(getPendingMembersQuery)
  const { mutateAsync: updateUser, isPending: isPendingUpdateUser } = useMutation(updateUserMutation)

  const members = dataMembers?.members ?? []
  const pendingMembers = dataPendingMembers?.members ?? []
  const membersList = currentTabValue === TabItemValues.members ? members : pendingMembers

  const membersPaged = useMemo(() => {
    return membersList.slice(page * MEMBERS_PER_PAGE, (page + 1) * MEMBERS_PER_PAGE)
  }, [membersList, page])
  const checkedMembers = membersList.filter(({ id }) => checkedMemberIds.has(id))

  const {
    isDialogOpen: isInviteMemberDialogOpen,
    openDialog: openInviteMemberDialog,
    closeDialog: closeInviteMemberDialog,
  } = useDialog()
  const {
    isDialogOpen: isMemberDeleteDialogOpen,
    openDialog: openMemberDeleteDialog,
    closeDialog: closeMemberDeleteDialog,
  } = useDialog()
  const {
    isDialogOpen: isMemberTypeGuideDialogOpen,
    openDialog: openMemberTypeGuideDialog,
    closeDialog: closeMemberTypeGuideDialog,
  } = useDialog()

  const totalPages = Math.ceil(membersList.length / MEMBERS_PER_PAGE)
  const showsPagination = membersList?.length > MEMBERS_PER_PAGE
  const selectableMembers = membersPaged.filter(
    ({ id, role }) => role !== UserRole.SUPER_ADMIN && role !== UserRole.ADMIN && id !== user?.user.id
  )
  const isAllChecked = checkedMemberIds.size > 0 && selectableMembers.length === checkedMemberIds.size

  const handleRoleChange = useCallback(
    async (memberId: number, role: UserRole) => {
      await updateUser({ userId: memberId, role })
      await queryClient.invalidateQueries(getMembersQuery)
    },
    [updateUser]
  )

  const handleChangeTabValue = (value: string) => {
    setCurrentTabValue(value as TabItemValues)
  }

  const handleChangeAllCheck = (isAllChecked: boolean) => {
    if (isAllChecked) {
      const memberIds = selectableMembers.map(({ id }) => id)
      setCheckedMemberIds(new Set(memberIds))
    } else {
      setCheckedMemberIds(new Set())
    }
  }

  const handleChangeMemberChecked = (id: number) => {
    if (id === user?.user.id) {
      return
    }

    setCheckedMemberIds((checkedMemberIds) => {
      const newCheckedMemberIds = new Set(checkedMemberIds)
      if (newCheckedMemberIds.has(id)) {
        newCheckedMemberIds.delete(id)
      } else {
        newCheckedMemberIds.add(id)
      }
      return newCheckedMemberIds
    })
  }

  const handleClickMemberDeleteButton = () => {
    if (checkedMemberIds.size === 0) {
      return
    }

    openMemberDeleteDialog()
  }

  const resetCheckedMemberIds = () => {
    setCheckedMemberIds(new Set())
  }

  return {
    currentTabValue,
    membersPaged,
    page,
    totalPages,
    checkedMembers,
    checkedMemberIds,
    isInviteMemberDialogOpen,
    isMemberDeleteDialogOpen,
    isMemberTypeGuideDialogOpen,
    isPendingUpdateUser,
    isAllChecked,
    showsPagination,
    setPage,
    openInviteMemberDialog,
    openMemberDeleteDialog,
    openMemberTypeGuideDialog,
    closeInviteMemberDialog,
    closeMemberDeleteDialog,
    closeMemberTypeGuideDialog,
    resetCheckedMemberIds,
    handleRoleChange,
    handleChangeTabValue,
    handleChangeAllCheck,
    handleChangeMemberChecked,
    handleClickMemberDeleteButton,
  }
}
